import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Column } from "react-table";
import { Col, Row } from 'reactstrap';
import Toast from "../../helpers/Toast";
import { SearchByCounterTypeNameDTO } from '../../models/Counter/SearchByCounterTypeNameDTO';
import { AssociateCounterToEquipmentDTO } from "../../models/Equipment/AssociateCounterToEquipmentDTO";
import { UnbindCounterFromEquipmentDTO } from "../../models/Equipment/UnbindCounterFromEquipmentDTO";
import { CountersFromEquipmentRequestDTO } from "../../models/Equipment/CountersFromEquipmentRequestDTO";
import { PaginatedEquipmentCounterDTO } from "../../models/Equipment/PaginatedEquipmentCounterDTO";
import { CounterTypeService } from '../../services/CounterTypeService';
import { EquipmentService } from "../../services/EquipmentService";
import ButtonComponent from "../ButtonComponent";
import ModalComponent from "../ModalComponent";
import { SelectItemsProps } from '../SearchOrTypeSelectComponent';
import SearchSelectComponent from '../SearchSelectComponent';
import TableComponent from "../TableComponent";
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';

interface EquipmentCountersProps {
  equipmentId: number;
}

function EquipmentCounters({ equipmentId }: EquipmentCountersProps) {
  const intl = useIntl();
  const { availableModules } = useAvailableModulesContext();
  const equipmentService = React.useMemo(() => new EquipmentService(), []);
  const counterService = React.useMemo(() => new CounterTypeService(), []);
  const [data, setData] = useState<PaginatedEquipmentCounterDTO[]>([]);
  const pageSizes: number[] = [5, 15, 30];
  const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAssociating, setIsAssociating] = useState<boolean>(false);
  const [isUnbinding, setIsUnbinding] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [counterToUnbind, setCounterToUnbind] = useState<{ id: number, name: string } | null>(null);
  const [goToPage, setGoToPage] = useState<number>();
  const [pageCount, setPageCount] = useState(1);
  const [counterSelected, setCounterSelected] = useState<SelectItemsProps | null>(null);

  const columns = React.useMemo<Column<object>[]>(() => [
    {
      Header: <FormattedMessage id="Screens.CounterTypes.Table.Code" />,
      accessor: "code",
      width: 20,
    },
    {
      Header: <FormattedMessage id="Screens.CounterTypes.Table.Name" />,
      accessor: "name",
      width: 35,
    },
    {
      Header: <FormattedMessage id="Screens.CounterTypes.Table.Unit" />,
      accessor: "unit",
      width: 20,
      Cell: ({ value }: any) => <span>{value ? value : "-"}</span>,
    },
    {
      Header: <FormattedMessage id="Screens.Equipment.Counters.Actions" />,
      accessor: "actions",
      width: 5,
      disableSortBy: true,
      Cell: ({ row }: any) => {
        const counter = row.original as PaginatedEquipmentCounterDTO;

        // Only show unbind button when ERP counters are not active
        if (availableModules.includes("IsActiveErpEquipmentCountersModule")) {
          return null;
        }

        return (
          <ButtonComponent
            text={intl.formatMessage({ id: "Screens.Equipment.Counters.Unbind" })}
            onClick={() => showUnbindConfirmation(counter.counterTypeId, counter.name)}
            loading={isUnbinding === counter.counterTypeId}
            disabled={isUnbinding !== null}
          />
        );
      },
    },
  ], [isUnbinding, intl, availableModules]);

  const getCounters = async (
    counterName: string
  ): Promise<SelectItemsProps[]> => {
    var companyNameDTO: SearchByCounterTypeNameDTO = {
      name: counterName
    };
    const response = await counterService.SearchByName(companyNameDTO);
    if (response.success === false) {
      Toast.Show("error", response.message);
    }
    const list: SelectItemsProps[] = [];
    response.obj?.forEach((element) => {
      var item: SelectItemsProps = {
        label: `${element.code} - ${element.name}`,
        value: `${element.id}`
      }
      list.push(item);
    });

    return list;
  };

  const fetchEquipmentCounters = async (page: number, pageSize: number) => {
    setIsLoading(true);

    const search: CountersFromEquipmentRequestDTO = {
      equipmentId: equipmentId,
      currentPage: currentPage,
      pageSize: pageSize,
    }

    var response = await equipmentService.GetCountersFromEquipment(search);

    if (response.success === false) {
      Toast.Show("error", response.message);
      setIsLoading(false);
      return;
    }

    setData(response.items);
    setIsLoading(false);
    setPageCount(response.totalPages);
    setPageSize(pageSize);
    setCurrentPage(page);
    setGoToPage(0)
  }

  const handleAssociateCounter = async () => {
    if (!counterSelected || !counterSelected.value) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Equipment.Counters.SelectCounter" }));
      return;
    }

    setIsAssociating(true);

    const associateCounterData: AssociateCounterToEquipmentDTO = {
      equipmentId: equipmentId,
      counterTypeId: parseInt(counterSelected.value)
    };

    try {
      const response = await equipmentService.AssociateCounter(associateCounterData);

      if (response.success) {
        Toast.Show("success", intl.formatMessage({ id: "Screens.Equipment.Counters.AssociatedSuccess" }));
        setCounterSelected(null);
        fetchEquipmentCounters(currentPage, pageSize);
      } else {
        Toast.Show("error", response.message || intl.formatMessage({ id: "Screens.Equipment.Counters.AssociatedError" }));
      }
    } catch (error) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Equipment.Counters.AssociatedError" }));
    } finally {
      setIsAssociating(false);
    }
  };

  const showUnbindConfirmation = (counterTypeId: number, counterName: string) => {
    setCounterToUnbind({ id: counterTypeId, name: counterName });
    setShowConfirmDialog(true);
  };

  const confirmUnbind = () => {
    if (counterToUnbind) {
      handleUnbindCounter(counterToUnbind.id);
      setShowConfirmDialog(false);
      setCounterToUnbind(null);
    }
  };

  const cancelUnbind = () => {
    setShowConfirmDialog(false);
    setCounterToUnbind(null);
  };

  const handleUnbindCounter = async (counterTypeId: number) => {
    setIsUnbinding(counterTypeId);

    const unbindCounterData: UnbindCounterFromEquipmentDTO = {
      equipmentId: equipmentId,
      counterTypeId: counterTypeId
    };

    try {
      const response = await equipmentService.UnbindCounter(unbindCounterData);

      if (response.success) {
        Toast.Show("success", intl.formatMessage({ id: "Screens.Equipment.Counters.UnbindSuccess" }));
        fetchEquipmentCounters(currentPage, pageSize);
      } else {
        Toast.Show("error", response.message || intl.formatMessage({ id: "Screens.Equipment.Counters.UnbindError" }));
      }
    } catch (error) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.Equipment.Counters.UnbindError" }));
    } finally {
      setIsUnbinding(null);
    }
  };

  useEffect(() => {
    if (equipmentId !== 0) {
      fetchEquipmentCounters(currentPage ?? 1, pageSize ?? 5)
    }
  }, [equipmentId, currentPage, pageSize])

  return (
    <div>
      {!availableModules.includes("IsActiveErpEquipmentCountersModule") && (
        <Row>
          <SearchSelectComponent
            xl={4}
            lg={8}
            md={8}
            sm={8}
            xs={8}
            label={intl.formatMessage({
              id: "Screens.Equipment.Counters.Name",
            })}
            loadOptions={getCounters}
            onChange={(e) => {
              setCounterSelected(e);
            }}
            value={counterSelected}
            style={{ width: "100%" }}
          />
          <Col
            xl={2}
            lg={2}
            md={2}
            sm={2}
            xs={2}
            style={{ marginTop: "21.8px", alignSelf: "center" }}>
            <ButtonComponent
              text={intl.formatMessage({ id: "Screens.Equipment.Counters.Associate" })}
              onClick={handleAssociateCounter}
              loading={isAssociating}
              disabled={isAssociating || !counterSelected}
            />
          </Col>
        </Row>
      )}

      <p style={{ margin: "0px", padding: "13px" }}><b><FormattedMessage id="Screens.Equipment.Counters" /></b></p>
      <TableComponent
        columns={columns}
        data={data}
        goToPage={goToPage}
        initialPage={currentPage}
        pageCount={pageCount}
        initialPageSize={pageSize}
        pageSizes={pageSizes}
        manualPagination={true}
        loading={isLoading}
        fetchData={fetchEquipmentCounters}
        initialSortBy={[]}
        disableSortBy={true}
      />

      <ModalComponent
        IsOpen={showConfirmDialog}
        title={intl.formatMessage({ id: "Screens.Equipment.Counters.UnbindConfirmTitle" })}
        text={intl.formatMessage(
          { id: "Screens.Equipment.Counters.UnbindConfirmMessage" },
          { counterName: counterToUnbind?.name || "" }
        )}
        buttonLeft={intl.formatMessage({ id: "General.Cancel" })}
        buttonRight={intl.formatMessage({ id: "General.Confirm" })}
        OnClickLeft={cancelUnbind}
        OnClickRight={confirmUnbind}
      />
    </div>
  );
}

export default EquipmentCounters;
